import React, { useState } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LineChart, BarChart3, BrainCircuit, CheckCircle2, ArrowRight } from 'lucide-react';
import { useSound } from '../hooks/useSound';
import { DailyIntelligence } from './products/DailyIntelligence';
import { StrategicPlanning } from './products/StrategicPlanning';
import { FinancialOptimization } from './products/FinancialOptimization';
import { SEO } from '../components/SEO';

const products = [
  {
    path: 'daily-intelligence',
    icon: BrainCircuit,
    title: 'Daily Business Intelligence',
    description: 'Stay ahead of market trends and optimize decision-making with AI-powered insights',
    features: [
      'AI-driven daily insights tailored to your business',
      'Real-time performance tracking across key areas',
      'Customized alerts and recommendations',
      'Ongoing AI analysis for strategy refinement'
    ]
  },
  {
    path: 'strategic-planning',
    icon: BarChart3,
    title: 'Strategic Growth Planning',
    description: 'Develop and execute data-backed strategies for sustainable growth',
    features: [
      'Comprehensive business assessment',
      'Custom execution roadmap',
      'Market positioning recommendations',
      'AI-powered progress monitoring'
    ]
  },
  {
    path: 'financial-optimization',
    icon: LineChart,
    title: 'AI-Powered Financial Optimization',
    description: 'Leverage AI insights to enhance financial decision-making',
    features: [
      'Advanced profitability analysis',
      'Cash flow forecasting',
      'Risk assessment tools',
      'Automated financial health monitoring'
    ]
  }
];

const springConfig = {
  type: "spring",
  stiffness: 50,
  damping: 20,
  mass: 1
};

export const Products = () => {
  const navigate = useNavigate();
  const { playHover, playClick } = useSound();

  return (
    <div className="pt-24 px-4 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <Routes>
          <Route
            path="/"
            element={
              <>
                <SEO
                  title="AI-Powered Business Solutions | ProfitIQ.AI"
                  description="Transform your business with our suite of AI-driven solutions. Get real-time insights, strategic planning tools, and financial optimization powered by advanced artificial intelligence."
                />
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, ...springConfig }}
                  className="text-center mb-16"
                >
                  <h1 className="text-4xl font-bold mb-4">
                    <span className="bg-gradient-to-r from-amber-200 to-amber-400 text-transparent bg-clip-text">
                      AI-Powered Business Solutions
                    </span>
                  </h1>
                  <p className="text-gray-400 text-lg max-w-3xl mx-auto">
                    ProfitIQ.AI delivers a powerful suite of AI-driven solutions designed to help businesses optimize operations, maximize financial performance, and drive strategic growth. Our platform provides real-time business intelligence, actionable insights, and personalized execution support to ensure success.
                  </p>
                </motion.div>

                <div className="space-y-16">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {products.map((product, index) => (
                      <motion.div
                        key={product.path}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1, duration: 1, ...springConfig }}
                      >
                        <motion.div
                          whileHover={{ y: -5, scale: 1.02 }}
                          transition={{ duration: 0.8, ...springConfig }}
                          className="h-full p-6 bg-gradient-to-b from-amber-400/5 to-transparent rounded-xl border border-amber-400/10 backdrop-blur-sm group cursor-pointer"
                          onClick={() => {
                            playClick();
                            navigate(product.path);
                          }}
                          onMouseEnter={playHover}
                        >
                          <motion.div
                            whileHover={{ scale: 1.1 }}
                            transition={{ duration: 0.8, ...springConfig }}
                            className="w-12 h-12 mb-4 bg-amber-400/10 rounded-lg flex items-center justify-center group-hover:bg-amber-400/20 transition-colors duration-700"
                          >
                            <product.icon className="w-6 h-6 text-amber-400" />
                          </motion.div>
                          <h3 className="text-xl font-semibold text-white mb-2">{product.title}</h3>
                          <p className="text-gray-400 mb-4">{product.description}</p>
                          <ul className="space-y-2 mb-6">
                            {product.features.map((feature, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-gray-300">
                                <CheckCircle2 className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>
                          <motion.div
                            whileHover={{ x: 4 }}
                            className="flex items-center gap-2 text-amber-400 group-hover:text-amber-300 transition-colors duration-300"
                          >
                            Learn More
                            <ArrowRight className="w-4 h-4" />
                          </motion.div>
                        </motion.div>
                      </motion.div>
                    ))}
                  </div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 1, ...springConfig }}
                    className="text-center p-8 bg-gradient-to-b from-amber-400/5 to-transparent rounded-xl border border-amber-400/10 backdrop-blur-sm"
                  >
                    <h2 className="text-2xl font-bold text-white mb-4">Unlock Your Business Potential with AI-Driven Solutions</h2>
                    <p className="text-gray-400 mb-6 max-w-2xl mx-auto">
                      Whether you're looking to streamline operations, scale profitably, or gain a competitive edge, ProfitIQ.AI provides the expertise and intelligence to drive results.
                    </p>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      transition={springConfig}
                      onMouseEnter={playHover}
                      onClick={() => {
                        playClick();
                        navigate('/consultation');
                      }}
                      className="px-8 py-4 bg-gradient-to-r from-amber-400 to-amber-600 rounded-lg text-black font-semibold text-lg hover:shadow-lg hover:shadow-amber-400/20 transition-all duration-300"
                    >
                      Get Started
                    </motion.button>
                  </motion.div>
                </div>
              </>
            }
          />
          <Route path="daily-intelligence" element={<DailyIntelligence />} />
          <Route path="strategic-planning" element={<StrategicPlanning />} />
          <Route path="financial-optimization" element={<FinancialOptimization />} />
        </Routes>
      </div>
    </div>
  );
};